// src/types/businessContract.ts - Contrat annuel et paiements des clients (business) - Super Admin

import type { Timestamp } from 'firebase/firestore';

/** Statut du contrat annuel */
export type BusinessContractStatus = 'active' | 'expired' | 'renewal_pending';

/** Durée standard d'un contrat en mois */
export const CONTRACT_DURATION_MONTHS = 12;

/** Document Firestore brut (champs Timestamp) */
export interface BusinessContractDocument {
  businessId: string;
  startDate: Timestamp;
  endDate: Timestamp;
  monthlyAmount: number;
  currency?: string;
  status: BusinessContractStatus;
  description?: string | null;
  signedAt?: Timestamp | null;
  protocolAttachmentUrl?: string | null;
  createdAt: Timestamp;
  updatedAt?: Timestamp | null;
}

/** Type de paiement : mensualité, autre frais, ou mois offert (0€, avec période couverte) */
export type ContractPaymentType = 'monthly' | 'other' | 'complimentary';
export type ContractPaymentStatus = 'pending' | 'paid' | 'cancelled';

/** Document Firestore brut (champs Timestamp) */
export interface BusinessContractPaymentDocument {
  businessId: string;
  /** Date du règlement effectif — absente tant que l’échéance n’est pas payée */
  paymentDate?: Timestamp | null;
  /** Montant à régler (après remise éventuelle) */
  amount: number;
  /** Montant avant remise (si remise appliquée) */
  originalAmount?: number | null;
  /** Libellé commercial de la remise (ex. « Remise bloc 6 mois ») */
  discountLabel?: string | null;
  /** Pourcentage de remise saisi (informatif, pour réédition) */
  discountPercent?: number | null;
  paymentType: ContractPaymentType;
  status?: ContractPaymentStatus;
  coverageStartDate?: Timestamp | null;
  coverageEndDate?: Timestamp | null;
  feeLabel?: string | null;
  reference?: string | null;
  notes?: string | null;
  attachmentUrl?: string | null;
  createdAt: Timestamp;
  createdBy?: string | null;
}

function isFirestoreTimestamp(value: unknown): value is { toDate: () => Date } {
  if (value === null || typeof value !== 'object') return false;
  return typeof (value as { toDate?: unknown }).toDate === 'function';
}

/** Convertit un champ Firestore (Timestamp ou Date) en Date */
export function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (isFirestoreTimestamp(value)) return value.toDate();
  return undefined;
}

function parseContractStatus(value: unknown): BusinessContractStatus {
  if (value === 'active' || value === 'expired' || value === 'renewal_pending') return value;
  return 'active';
}

/** Convertit un document contrat Firestore en BusinessContract */
export function mapContractFromFirestore(
  id: string,
  data: Partial<BusinessContractDocument> & Pick<BusinessContractDocument, 'businessId'>
): BusinessContract {
  return {
    id,
    businessId: data.businessId,
    startDate: toDate(data.startDate) ?? new Date(),
    endDate: toDate(data.endDate) ?? new Date(),
    monthlyAmount: data.monthlyAmount ?? 0,
    currency: data.currency ?? 'FCFA',
    status: parseContractStatus(data.status),
    description: data.description ?? undefined,
    signedAt: toDate(data.signedAt ?? undefined),
    protocolAttachmentUrl: data.protocolAttachmentUrl ?? undefined,
    createdAt: toDate(data.createdAt) ?? new Date(),
    updatedAt: toDate(data.updatedAt ?? undefined),
  };
}

function parsePaymentType(value: unknown): ContractPaymentType {
  if (value === 'monthly' || value === 'other' || value === 'complimentary') return value;
  return 'monthly';
}

function parsePaymentStatus(value: unknown): ContractPaymentStatus {
  if (value === 'pending' || value === 'paid' || value === 'cancelled') return value;
  // Compat ascendante : les anciens paiements sans statut sont considérés payés.
  return 'paid';
}

/** Convertit un document paiement Firestore en BusinessContractPayment */
export function mapPaymentFromFirestore(
  id: string,
  data: Partial<BusinessContractPaymentDocument> & Pick<BusinessContractPaymentDocument, 'businessId'>
): BusinessContractPayment {
  const paymentType = parsePaymentType(data.paymentType);
  const paymentDate = toDate(data.paymentDate ?? undefined);
  const coverageStart = toDate(data.coverageStartDate ?? undefined);
  const coverageEnd = toDate(data.coverageEndDate ?? undefined);
  return {
    id,
    businessId: data.businessId,
    paymentDate,
    amount: data.amount ?? 0,
    paymentType,
    status: parsePaymentStatus(data.status),
    coverageStartDate:
      paymentType === 'monthly' || paymentType === 'complimentary'
        ? (coverageStart ?? paymentDate)
        : undefined,
    coverageEndDate:
      paymentType === 'monthly' || paymentType === 'complimentary'
        ? (coverageEnd ?? paymentDate)
        : undefined,
    feeLabel: data.feeLabel ?? undefined,
    originalAmount:
      typeof data.originalAmount === 'number' && data.originalAmount > 0
        ? data.originalAmount
        : undefined,
    discountLabel: data.discountLabel ?? undefined,
    discountPercent:
      typeof data.discountPercent === 'number' && data.discountPercent > 0
        ? data.discountPercent
        : undefined,
    reference: data.reference ?? undefined,
    notes: data.notes ?? undefined,
    attachmentUrl: data.attachmentUrl ?? undefined,
    createdAt: toDate(data.createdAt) ?? new Date(),
    createdBy: data.createdBy ?? undefined,
  };
}

/** Contrat annuel d'un business (1 an d'engagement, renouvelable) */
export interface BusinessContract {
  id: string;
  businessId: string;
  /** Date de début de l'engagement (signature du protocole) */
  startDate: Date;
  /** Date de fin du contrat (engagement) */
  endDate: Date;
  /** Mensualité en FCFA (varie par client) */
  monthlyAmount: number;
  /** Devise (par défaut FCFA) */
  currency?: string;
  status: BusinessContractStatus;
  /** Description ou objet du contrat (optionnel) */
  description?: string;
  /** Date de signature du protocole d'accord (optionnel) */
  signedAt?: Date;
  /** Référence ou lien vers le protocole signé (optionnel) */
  protocolAttachmentUrl?: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Échéance / paiement contrat : mensualité, autre frais ou mois offert (0€) */
export interface BusinessContractPayment {
  id: string;
  businessId: string;
  /** Date du règlement effectif — définie uniquement quand status = paid */
  paymentDate?: Date;
  /** Montant à régler (après remise éventuelle) */
  amount: number;
  /** Montant avant remise — si présent et > amount, une remise est affichée */
  originalAmount?: number;
  /** Libellé de la remise (visible client) */
  discountLabel?: string;
  /** % de remise (informatif / réédition formulaire) */
  discountPercent?: number;
  /** Type : mensualité, autre frais ou mois offert */
  paymentType: ContractPaymentType;
  /** Statut de règlement du paiement */
  status: ContractPaymentStatus;
  /** Début de la période couverte (mensualité et complimentary) */
  coverageStartDate?: Date;
  /** Fin de la période couverte (mensualité et complimentary) */
  coverageEndDate?: Date;
  /** Libellé : frais (other) ou motif offre (complimentary ex: Promotion) */
  feeLabel?: string;
  /** Référence (n° virement, reçu, etc.) */
  reference?: string;
  notes?: string;
  /** Lien vers pièce justificative (optionnel) */
  attachmentUrl?: string;
  /** Provider de paiement en ligne (renseigné par Cloud Function) */
  provider?: string;
  providerSessionId?: string;
  providerPaymentId?: string;
  initiatedByUserId?: string;
  createdAt: Date;
  createdBy?: string;
}

/** Dernier jour couvert = max(coverageEndDate) des mensualités et mois offerts */
export function getCoverageExpiryDate(payments: BusinessContractPayment[]): Date | null {
  const withCoverage = payments.filter((p) =>
    (
      p.paymentType === 'complimentary' ||
      (p.paymentType === 'monthly' && p.status === 'paid')
    ) && p.coverageEndDate
  );
  if (withCoverage.length === 0) return null;
  const dates = withCoverage.map((p) => (p.coverageEndDate as Date).getTime());
  return new Date(Math.max(...dates));
}

/** Statut effectif du contrat (prend en compte la date de fin) */
export function getEffectiveContractStatus(contract: BusinessContract): BusinessContractStatus {
  const end = contract.endDate instanceof Date ? contract.endDate : new Date(contract.endDate);
  if (end < new Date()) return 'expired';
  return contract.status;
}

/** Vérifie si le contrat est proche de l'expiration (J jours avant) */
export function isContractExpiringSoon(contract: BusinessContract, daysThreshold = 30): boolean {
  if (contract.status !== 'active') return false;
  const now = new Date();
  const end = contract.endDate instanceof Date ? contract.endDate : new Date(contract.endDate);
  const diffMs = end.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > 0 && diffDays <= daysThreshold;
}

/**
 * Retard de paiement (mois par mois) : le mois en cours n'est pas couvert par un paiement.
 * Basé uniquement sur la couverture des paiements (mensualités, mois offerts), PAS sur la période du contrat.
 * True si : pas de paiements avec couverture, ou le dernier mois couvert est avant le mois en cours.
 */
export function hasPaymentDelay(payments: BusinessContractPayment[]): boolean {
  const coverageExpiry = getCoverageExpiryDate(payments);
  return !coverageExpiry || coverageExpiry < new Date();
}

/**
 * Prochain paiement dans J jours : la couverture expire bientôt (dans les jours indiqués).
 * Ex: isPaymentDueWithinDays(payments, 7) = le dernier mois couvert expire dans ≤ 7 jours.
 */
/** Paiement(s) de type "other" (ex: frais d'installation) en attente de règlement (montant > 0) */
export function hasPendingOtherPayments(payments: BusinessContractPayment[]): boolean {
  return payments.some(
    (p) => p.paymentType === 'other' && p.status === 'pending' && (p.amount ?? 0) > 0
  );
}

/** Libellés des paiements "other" en attente (ex: Frais d'installation, Inventaire) */
export function getPendingOtherPaymentLabels(payments: BusinessContractPayment[]): string[] {
  const labels = payments
    .filter((p) => p.paymentType === 'other' && p.status === 'pending' && (p.amount ?? 0) > 0)
    .map((p) => p.feeLabel || 'Autres frais')
    .filter(Boolean);
  return Array.from(new Set(labels));
}

/** Nombre de mois calendaires couverts (inclus), ou 0 si pas de couverture. */
export function getCoverageMonthCount(payment: Pick<BusinessContractPayment, 'coverageStartDate' | 'coverageEndDate'>): number {
  if (!payment.coverageStartDate || !payment.coverageEndDate) return 0;
  const start = payment.coverageStartDate instanceof Date
    ? payment.coverageStartDate
    : new Date(payment.coverageStartDate);
  const end = payment.coverageEndDate instanceof Date
    ? payment.coverageEndDate
    : new Date(payment.coverageEndDate);
  const startY = start.getFullYear();
  const startM = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  return (endY - startY) * 12 + (endM - startM) + 1;
}

/** True si le paiement couvre plus d’un mois calendaire. */
export function isMultiMonthCoverage(payment: Pick<BusinessContractPayment, 'coverageStartDate' | 'coverageEndDate'>): boolean {
  return getCoverageMonthCount(payment) > 1;
}

/**
 * Libellé de période : "03/2026" ou "03/2026 → 08/2026 (6 mois)".
 */
export function formatContractPaymentCoverageLabel(
  payment: Pick<BusinessContractPayment, 'coverageStartDate' | 'coverageEndDate' | 'paymentType'>,
  formatMonth: (d: Date) => string = (d) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
): string {
  if (
    (payment.paymentType !== 'monthly' && payment.paymentType !== 'complimentary') ||
    !payment.coverageStartDate ||
    !payment.coverageEndDate
  ) {
    return '—';
  }
  const start = payment.coverageStartDate instanceof Date
    ? payment.coverageStartDate
    : new Date(payment.coverageStartDate);
  const end = payment.coverageEndDate instanceof Date
    ? payment.coverageEndDate
    : new Date(payment.coverageEndDate);
  const count = getCoverageMonthCount(payment);
  if (count <= 1) return formatMonth(start);
  return `${formatMonth(start)} → ${formatMonth(end)} (${count} mois)`;
}

/** Titre affichable : Mensualité / Bloc N mois / Frais / Offert. */
export function formatContractPaymentTitle(
  payment: Pick<
    BusinessContractPayment,
    'paymentType' | 'feeLabel' | 'coverageStartDate' | 'coverageEndDate'
  >,
  formatMonth: (d: Date) => string = (d) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
): string {
  if (payment.paymentType === 'other') {
    return payment.feeLabel?.trim() || 'Autres frais';
  }
  if (payment.paymentType === 'complimentary') {
    const base = payment.feeLabel?.trim() || 'Mois offert';
    const count = getCoverageMonthCount(payment);
    if (count > 1) return `${base} (${count} mois)`;
    return base;
  }
  const count = getCoverageMonthCount(payment);
  if (count > 1 && payment.coverageStartDate && payment.coverageEndDate) {
    const start = payment.coverageStartDate instanceof Date
      ? payment.coverageStartDate
      : new Date(payment.coverageStartDate);
    const end = payment.coverageEndDate instanceof Date
      ? payment.coverageEndDate
      : new Date(payment.coverageEndDate);
    return `Bloc ${count} mois (${formatMonth(start)} → ${formatMonth(end)})`;
  }
  if (payment.coverageStartDate) {
    const start = payment.coverageStartDate instanceof Date
      ? payment.coverageStartDate
      : new Date(payment.coverageStartDate);
    return `Mensualité ${formatMonth(start)}`;
  }
  return 'Mensualité';
}

/** Infos remise dérivées d’une échéance (pour UI admin / client). */
export function getContractPaymentDiscountInfo(
  payment: Pick<BusinessContractPayment, 'amount' | 'originalAmount' | 'discountLabel' | 'discountPercent'>
): {
  hasDiscount: boolean;
  originalAmount: number;
  amount: number;
  discountAmount: number;
  discountPercent: number | null;
  discountLabel: string | null;
} {
  const amount = payment.amount ?? 0;
  const original =
    typeof payment.originalAmount === 'number' && payment.originalAmount > amount
      ? payment.originalAmount
      : amount;
  const discountAmount = Math.max(0, original - amount);
  const hasDiscount = discountAmount > 0 && original > amount;
  const discountPercent =
    hasDiscount && original > 0
      ? payment.discountPercent && payment.discountPercent > 0
        ? payment.discountPercent
        : Math.round((discountAmount / original) * 1000) / 10
      : null;
  return {
    hasDiscount,
    originalAmount: original,
    amount,
    discountAmount,
    discountPercent,
    discountLabel: hasDiscount ? (payment.discountLabel?.trim() || 'Remise') : null,
  };
}

/**
 * Calcule le montant final après remise.
 * - mode `none` : pas de remise
 * - mode `amount` : soustrait discountValue du sous-total
 * - mode `percent` : soustrait discountValue % du sous-total
 */
export function applyContractPaymentDiscount(
  subtotal: number,
  mode: 'none' | 'amount' | 'percent',
  discountValue: number
): { originalAmount: number; amount: number; discountAmount: number; discountPercent: number | null } {
  const originalAmount = Math.max(0, subtotal);
  if (mode === 'none' || !discountValue || discountValue <= 0 || originalAmount <= 0) {
    return { originalAmount, amount: originalAmount, discountAmount: 0, discountPercent: null };
  }
  let discountAmount = 0;
  let discountPercent: number | null = null;
  if (mode === 'percent') {
    discountPercent = Math.min(100, discountValue);
    discountAmount = Math.round((originalAmount * discountPercent) / 100);
  } else {
    discountAmount = Math.min(originalAmount, discountValue);
    discountPercent = originalAmount > 0
      ? Math.round((discountAmount / originalAmount) * 1000) / 10
      : null;
  }
  return {
    originalAmount,
    amount: Math.max(0, originalAmount - discountAmount),
    discountAmount,
    discountPercent,
  };
}

/**
 * Échéances payables en ligne : créées côté admin (CSM / Super Admin), statut pending,
 * hors mois offerts (complimentary).
 */
export function getPendingPayablePayments(
  payments: BusinessContractPayment[]
): BusinessContractPayment[] {
  return payments
    .filter(
      (p) =>
        p.status === 'pending' &&
        p.paymentType !== 'complimentary' &&
        (p.amount ?? 0) > 0
    )
    .sort((a, b) => {
      const ta = a.coverageStartDate?.getTime() ?? a.createdAt.getTime();
      const tb = b.coverageStartDate?.getTime() ?? b.createdAt.getTime();
      return ta - tb;
    });
}

export function isPaymentDueWithinDays(payments: BusinessContractPayment[], days = 7): boolean {
  const coverageExpiry = getCoverageExpiryDate(payments);
  if (!coverageExpiry) return false;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const expiryDayEnd = new Date(coverageExpiry);
  expiryDayEnd.setHours(23, 59, 59, 999);
  if (expiryDayEnd < todayStart) return false;
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.ceil((expiryDayEnd.getTime() - todayStart.getTime()) / msPerDay);
  return diffDays >= 0 && diffDays <= days;
}
